import { Stack, Text, mergeStyleSets, Checkbox, MessageBar, MessageBarType } from '@fluentui/react';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { FunctionOr } from '../../../../../types/FunctionOr';

const styles = mergeStyleSets({
    description: {
        fontSize: '14px',
        opacity: 0.8,
        marginBottom: '30px',
        fontWeight: 500,
    },
    checkboxContainer: {
        marginBottom: '16px',
    },
    checkboxLabel: {
        fontWeight: 600,
    },
    checkboxDescription: {
        fontSize: '12px',
        opacity: 0.5,
        marginTop: '4px',
        marginLeft: '28px',
    },
    disclaimerMessage: {
        marginBottom: '16px',
    },
});

export const AdminConfiguration = ({ user: userToEdit, setUser }: { user: TimeUser; setUser: (update: FunctionOr<TimeUser>) => void }) => {
    const {
        initialData: {
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const isCurrentUser = user.id === userToEdit.id;

    // Mutation for setting admin status
    const { mutate: setIsAdmin } = useMutation({
        mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) => ApiCalls.setIsAdmin({ userId, isAdmin }),
        onMutate: ({ isAdmin, userId }) => {
            setUser(user => ({ ...user, isAdmin }));
        },
    });

    // Handle admin status change
    const handleAdminStatusChange = (checked: boolean) => {
        // Only allow changing admin status for other users
        // Or if it's the current user and they're trying to add admin status (not remove it)
        if (!isCurrentUser || (isCurrentUser && !userToEdit.isAdmin && checked)) {
            setIsAdmin({ userId: userToEdit.id, isAdmin: checked });
        }
    };

    // Determine if the checkbox should be disabled
    // Disable if it's the current user AND they are an admin (to prevent removing admin from self)
    const isCheckboxDisabled = isCurrentUser && userToEdit.isAdmin;

    return (
        <Stack>
            <Text styles={{ root: styles.description }}>Manage user access rights and security settings.</Text>
            {isCurrentUser && userToEdit.isAdmin && (
                <MessageBar messageBarType={MessageBarType.info} isMultiline={false} styles={{ root: styles.disclaimerMessage }}>
                    You cannot remove admin access from your own account to prevent accidental lockout.
                </MessageBar>
            )}
            <Stack styles={{ root: styles.checkboxContainer }}>
                <Checkbox
                    label="Administrator"
                    checked={userToEdit.isAdmin}
                    onChange={(_, checked) => handleAdminStatusChange(checked)}
                    disabled={isCheckboxDisabled}
                    styles={{
                        label: styles.checkboxLabel,
                    }}
                />
                <Text styles={{ root: styles.checkboxDescription }}>Full system access and user management capabilities</Text>
            </Stack>
        </Stack>
    );
};
